import type { v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import { Loader } from '@deque/cauldron-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageTitle from '../../../common/components/PageTitle';
import { useLoadedConfiguration } from '../../../common/contexts/Configuration';
import { useProducts } from '../../../common/contexts/products';
import Form from './Form';
import RadioSetting from './RadioSetting';

interface AxeMonitorConfigurationProps {
  product: v2.Product;
}

enum CodeSnippets {
  on = 'on',
  off = 'off'
}

interface Form {
  saveSelectorsAndSnippets: CodeSnippets;
}

const AxeMonitorConfiguration: React.FC<AxeMonitorConfigurationProps> = ({
  product
}) => {
  const { t } = useTranslation();
  const {
    settings,
    updateSettings,
    saving,
    isEnterpriseMember,
    isEnterpriseAdmin,
    loadingUserEnterpriseSettings,
    updateSettingsShouldUpdateKey
  } = useLoadedConfiguration();
  const { getProductBySlug } = useProducts();
  const location = useLocation();
  const axeMonitorSettings = settings.axeMonitor;
  const { control, handleSubmit, reset, formState } = useForm<Form>();
  const isReadOnly = isEnterpriseMember && !isEnterpriseAdmin;
  const disabled = isReadOnly || saving;

  useEffect(() => {
    if (axeMonitorSettings) {
      reset({
        saveSelectorsAndSnippets: axeMonitorSettings.saveSelectorsAndSnippets
          .value
          ? CodeSnippets.on
          : CodeSnippets.off
      });
    }
  }, [axeMonitorSettings]);

  React.useEffect(() => {
    updateSettingsShouldUpdateKey(location.key);
  }, [location.key]);

  const onSubmit = (data: Form) => {
    updateSettings({
      axeMonitor: {
        saveSelectorsAndSnippets: {
          value: data.saveSelectorsAndSnippets === CodeSnippets.on,
          type: 'default'
        }
      }
    });
  };

  const onDiscard = () => {
    reset();
  };

  return (
    <>
      <PageTitle
        title={t('Configuration - {{productName}}', {
          productName: getProductBySlug(ProductSlugs.axeMonitor)?.name
        })}
      />

      <Form
        header={product.name}
        onDiscard={onDiscard}
        onSave={handleSubmit(onSubmit)}
        isDirty={formState.isDirty}
        isReadOnly={isReadOnly}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {loadingUserEnterpriseSettings ? (
            <Loader
              title={t('Loading {{ axeMonitor }} settings...', {
                axeMonitor: product.name
              })}
            ></Loader>
          ) : (
            <fieldset>
              <RadioSetting
                id="save-selectors-and-snippets"
                name="saveSelectorsAndSnippets"
                label={t('Code Snippets')}
                description={t(
                  "Deque recommends keeping this setting turned on to better understand and prioritize accessibility issues. Turning this setting off will restrict users' access to information in selectors, source code, and page content. This will limit their ability to fix accessibility issues."
                )}
                shouldShowAllowChange={false}
                options={[
                  {
                    id: CodeSnippets.on,
                    label: t('On (recommended)'),
                    value: CodeSnippets.on
                  },
                  {
                    id: CodeSnippets.off,
                    label: t('Off'),
                    value: CodeSnippets.off
                  }
                ]}
                formControl={control}
                disabled={disabled}
              />
            </fieldset>
          )}
        </form>
      </Form>
    </>
  );
};

export default AxeMonitorConfiguration;
